import { useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { format, parseISO } from 'date-fns';
import { Info as InfoIcon } from 'lucide-react';
import { BrandHealthDateFilter } from './date-filter';
import { BrandHealthFilterSelect } from './filter-select';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuth } from '@/hooks';
import {
  useBrandHealthFilters,
  useBrandHealthOverview,
  useStableBrandHealthQuery,
  type BrandHealthOverviewState,
} from '@/hooks/use-brand-health';
import type {
  BrandHealthDurationStats,
  BrandHealthOverview,
  BrandHealthRateMetric,
  BrandHealthSpeedMetric,
} from '@/types/brandHealth';
import { cn } from '@/lib/utils';
import { formatDuration } from './format';
import { brandHealthDateRangeError, toBrandHealthDateRange } from './date-range';
import { CHANNEL_GROUPS, expandChannels, groupChannels } from './channels';
import { useBrandHealthSelection } from '@/hooks/use-brand-health-selection';

type TimeMode = 'business' | 'calendar';
type BusinessTimeAvailability = boolean | null;

const MAX_FILTER_VALUES = 50;
const FILTER_TRIGGER_CLASS = 'h-8 min-h-8 w-full rounded-full border-gray-300 bg-white px-3 text-xs text-gray-800 shadow-none';

function percent(value: number | null): string {
  return value === null ? '—' : `${Math.round(value * 100)}%`;
}

function Info({ label }: { label: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type='button'
          aria-label={label}
          className='inline-flex size-6 shrink-0 items-center justify-center rounded-full text-gray-800 hover:text-black-300 focus-visible:outline-2 focus-visible:outline-offset-2'
        >
          <InfoIcon aria-hidden='true' className='size-3.5' strokeWidth={1.5} />
        </button>
      </TooltipTrigger>
      <TooltipContent variant='dark' side='bottom' align='start' sideOffset={5} className='max-w-[min(280px,calc(100vw-2rem))] rounded-md px-3 py-2 text-left text-xs leading-relaxed'>{label}</TooltipContent>
    </Tooltip>
  );
}

function MetricCard({ title, help, className, children, testId }: {
  title: string;
  help: string;
  className?: string;
  children: ReactNode;
  testId?: string;
}) {
  return (
    <section data-testid={testId} className={cn('relative flex min-w-0 flex-col rounded-3xl border border-gray-500 bg-white px-4 py-3', className)}>
      <div className='flex items-center gap-1 text-sm font-normal leading-5 text-ink'>
        <h3>{title}</h3>
        <Info label={help} />
      </div>
      {children}
    </section>
  );
}

const SKELETON_CARD_CLASS = 'relative flex min-w-0 flex-col rounded-3xl border border-gray-500 bg-white px-4 py-3';

function SkeletonRows({ withToggle }: { withToggle?: boolean }) {
  return (
    <div data-testid='brand-health-skeleton-card' className={cn(SKELETON_CARD_CLASS, 'min-h-[236px]')}>
      <div className='flex items-center justify-between'>
        <Skeleton className='h-4 w-28' />
        {withToggle ? <Skeleton className='h-6 w-44 rounded-lg' /> : null}
      </div>
      <div className='mt-2 grid flex-1 grid-rows-3'>
        {[0, 1, 2].map((item) => (
          <div key={item} className={cn('flex min-h-[58px] items-center justify-between', item > 0 && 'border-t border-dashed border-gray-300')}>
            <Skeleton className='h-4 w-24' />
            <Skeleton className='h-7 w-12' />
          </div>
        ))}
      </div>
    </div>
  );
}

function LoadingDashboard() {
  return (
    <div role='status' aria-label='Loading Brand Health metrics'>
      <div className='mt-3 grid gap-4 lg:grid-cols-2'>
        <div data-testid='brand-health-skeleton-card' className={cn(SKELETON_CARD_CLASS, 'h-28')}>
          <Skeleton className='h-4 w-40' />
          <Skeleton className='mt-auto h-7 w-24' />
          <Skeleton className='absolute right-4 top-4 size-20 rounded-full' />
        </div>
        <div data-testid='brand-health-skeleton-card' className={cn(SKELETON_CARD_CLASS, 'min-h-28 lg:h-28')}>
          <Skeleton className='h-4 w-20' />
          <Skeleton className='mt-3 h-3 w-full rounded-full' />
          <div className='mt-2 flex gap-4'>
            {[0, 1, 2].map((item) => <Skeleton key={item} className='h-3 w-14' />)}
          </div>
        </div>
      </div>

      <Skeleton className='mt-11 h-4 w-28' />
      <div className='mt-3 grid gap-4 lg:grid-cols-2'>
        {[0, 1].map((item) => (
          <div key={item} data-testid='brand-health-skeleton-card' className={cn(SKELETON_CARD_CLASS, 'h-28')}>
            <Skeleton className='h-4 w-32' />
            <Skeleton className='mt-auto h-7 w-20' />
          </div>
        ))}
      </div>

      <div className='mt-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]'>
        <SkeletonRows />
        <SkeletonRows withToggle />
        <SkeletonRows withToggle />
      </div>
    </div>
  );
}

function OverviewStateShell({ overview }: { overview: BrandHealthOverviewState }) {
  if (overview.isLoading && !overview.data) return <LoadingDashboard />;
  if (overview.isError) {
    return (
      <>
        <div role='alert' className='mt-3 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3'>
          <div>
            <p className='text-sm font-medium text-black-300'>Brand Health metrics could not be loaded</p>
            <p className='text-xs text-gray-900'>The dashboard will fill in when the connection recovers.</p>
          </div>
          <Button size='sm' onClick={overview.refetch}>Try again</Button>
        </div>
        <LoadingDashboard />
      </>
    );
  }
  if (overview.data?.volume.totalTickets === 0) {
    return (
      <div className='mt-6 flex min-h-48 flex-col items-center justify-center rounded-[22px] border border-gray-300 px-6 text-center'>
        <p className='font-medium text-black-300'>No tickets in this range</p>
        <p className='mt-1 text-sm text-gray-900'>Choose a wider date range or clear a filter.</p>
      </div>
    );
  }
  return null;
}

const CHANNEL_COLOURS = ['bg-indigo-700', 'bg-indigo-300', 'bg-indigo-25'];

function ChannelDistribution({ channels }: { channels: BrandHealthOverview['volume']['channels'] }) {
  const ordered = [...channels].sort((a, b) => b.share - a.share);
  return (
    <div className='mt-2'>
      <div aria-hidden='true' className='mb-1.5 flex text-xs tabular-nums text-gray-700'>
        {ordered.map((item) => (
          <span key={item.channel} className='min-w-0 truncate pr-1' style={{ width: `${item.share * 100}%` }}>
            {item.count.toLocaleString()}
          </span>
        ))}
      </div>
      <div className='flex h-3 overflow-hidden rounded-full bg-gray-100' aria-label='Ticket distribution by channel'>
        {ordered.map((item, index) => (
          <div
            key={item.channel}
            className={cn('min-w-1 border-r-2 border-white last:border-r-0', CHANNEL_COLOURS[Math.min(index, CHANNEL_COLOURS.length - 1)])}
            style={{ width: `${item.share * 100}%` }}
          />
        ))}
      </div>
      <ul className='mt-2 flex flex-wrap gap-x-4 gap-y-1'>
        {ordered.map((item, index) => (
          <li key={item.channel} aria-label={`${item.channel}: ${item.count.toLocaleString()} tickets`} title={`${item.channel}: ${item.count.toLocaleString()} tickets`} className='flex items-center gap-1.5 text-xs text-gray-900'>
            <span className={cn('size-2.5 rounded-full', CHANNEL_COLOURS[Math.min(index, CHANNEL_COLOURS.length - 1)])} />
            <span className='capitalize'>{item.channel}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TimeModeToggle({ value, onChange, businessAvailable }: {
  value: TimeMode;
  onChange: (mode: TimeMode) => void;
  businessAvailable: boolean;
}) {
  return (
    <div className='flex rounded-lg bg-gray-100 p-0.5 text-[11px]'>
      <button
        type='button'
        aria-label='Business hours'
        aria-pressed={value === 'business'}
        disabled={!businessAvailable}
        title={!businessAvailable ? 'Business hours are not configured for this workspace.' : undefined}
        onClick={() => onChange('business')}
        className={cn('rounded-[6px] px-2.5 py-0.5 text-gray-800 focus-visible:outline-2 focus-visible:outline-offset-1', value === 'business' && 'bg-white text-black-300 shadow-sm')}
      >
        Business hours
      </button>
      <button
        type='button'
        aria-label='Calendar hours'
        aria-pressed={value === 'calendar'}
        onClick={() => onChange('calendar')}
        className={cn('rounded-[6px] px-2.5 py-0.5 text-gray-800 focus-visible:outline-2 focus-visible:outline-offset-1', value === 'calendar' && 'bg-white text-black-300 shadow-sm')}
      >
        Calendar hours
      </button>
    </div>
  );
}

function SpeedCard({ title, metric, help, testId, businessTimeAvailability }: {
  title: string;
  metric: BrandHealthSpeedMetric;
  help: string;
  testId: string;
  businessTimeAvailability: BusinessTimeAvailability;
}) {
  const [selectedMode, setSelectedMode] = useState<TimeMode>(businessTimeAvailability ? 'business' : 'calendar');
  const mode = businessTimeAvailability ? selectedMode : 'calendar';
  const stats: BrandHealthDurationStats | null = metric[mode];
  const rows = [
    ['Average', stats?.averageMs],
    ['Typical (P50)', stats?.p50Ms],
    ['Slowest 10% (P90)', stats?.p90Ms],
  ] as const;
  return (
    <MetricCard title={title} help={help} testId={testId} className='min-h-[236px]'>
      {businessTimeAvailability === null ? (
        <p className='mt-8 text-sm text-gray-800'>No speed samples in this range</p>
      ) : (
        <div className='mt-3 flex justify-start xl:-mt-6 xl:justify-end'>
          <TimeModeToggle value={mode} onChange={setSelectedMode} businessAvailable={businessTimeAvailability} />
        </div>
      )}
      {businessTimeAvailability === false ? (
        <p className='mt-3 text-xs text-gray-800'>Business hours are not configured for this workspace.</p>
      ) : null}
      {businessTimeAvailability !== null ? (
        <dl className='mt-2 grid flex-1 grid-rows-3'>
          {rows.map(([label, value], index) => (
            <div key={label} className={cn('flex min-h-[58px] items-center justify-between', index > 0 && 'border-t border-dashed border-gray-300')}>
              <dt className='text-sm text-gray-900'>{label}</dt>
              <dd className='text-xl font-semibold leading-7 tabular-nums text-ink'>{value === undefined ? '—' : formatDuration(value)}</dd>
            </div>
          ))}
        </dl>
      ) : null}
    </MetricCard>
  );
}

export function BrandHealthDashboard({ switcher }: { switcher?: ReactNode } = {}) {
  const { displayName, currentUser } = useAuth();
  const [selection, setSelection] = useBrandHealthSelection(`${currentUser?.id ?? 'anonymous'}:${currentUser?.accountId ?? 'unknown'}`);
  const { channels, categories } = selection;
  const startDate = useMemo(() => parseISO(selection.startDate), [selection.startDate]);
  const endDate = useMemo(() => parseISO(selection.endDate), [selection.endDate]);
  const range = useMemo(() => toBrandHealthDateRange(startDate, endDate), [startDate, endDate]);
  const dateRangeError = brandHealthDateRangeError(startDate, endDate);
  const filterError = channels.length > MAX_FILTER_VALUES
    ? `Select no more than ${MAX_FILTER_VALUES} channels`
    : categories.length > MAX_FILTER_VALUES
      ? `Select no more than ${MAX_FILTER_VALUES} categories`
      : null;
  const reportingError = dateRangeError ?? filterError;
  const filters = useBrandHealthFilters(dateRangeError ? null : range);
  const providerChannels = expandChannels(channels, filters.data?.channels ?? []);
  const query = useStableBrandHealthQuery(range, providerChannels, categories);
  const awaitingChannelOptions = channels.length > 0 && (filters.isLoading || filters.isError);
  const overview = useBrandHealthOverview(reportingError || awaitingChannelOptions ? null : query);
  const kimHandledTickets = overview.data?.volume.kimHandledTickets ?? 0;
  const filterControlsDisabled = Boolean(dateRangeError) || filters.isLoading || filters.isError;

  return (
    <main className='h-full w-full overflow-y-auto px-4 py-3 font-inter'>
      <header className='flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between'>
        <div>
          <h1 className='text-base font-semibold leading-5 text-ink'>Hello, {displayName}</h1>
          {switcher ?? <p className='text-xs leading-4 text-gray-700'>Dashboard</p>}
        </div>
        <div aria-label='Brand Health filters' className='grid w-full min-w-0 gap-2.5 lg:grid-cols-[236px_var(--channel-width)_var(--category-width)] lg:items-center xl:w-auto' style={{ '--channel-width': channels.length ? '160px' : '100px', '--category-width': categories.length ? '240px' : '104px' } as CSSProperties}>
          <BrandHealthDateFilter
            value={{ startDate, endDate }}
            onChange={(next) => {
              setSelection((previous) => ({ ...previous, startDate: next.startDate ? format(next.startDate, 'yyyy-MM-dd') : previous.startDate, endDate: next.endDate ? format(next.endDate, 'yyyy-MM-dd') : previous.endDate }));
            }}
          />
          <BrandHealthFilterSelect
            aria-label='Channel'
            options={CHANNEL_GROUPS.map((channel) => ({ label: channel[0].toUpperCase() + channel.slice(1), value: channel }))}
            defaultValue={channels}
            onValueChange={(channels) => setSelection((previous) => ({ ...previous, channels }))}
            placeholder='Channel'
            disabled={filterControlsDisabled}
            className={cn(FILTER_TRIGGER_CLASS, '[&_span]:mx-0 [&_svg]:mx-0 [&_svg]:shrink-0')}
          />
          <BrandHealthFilterSelect
            aria-label='Category'
            options={(filters.data?.categories ?? []).map((category) => ({ label: category, value: category }))}
            defaultValue={categories}
            onValueChange={(categories) => setSelection((previous) => ({ ...previous, categories }))}
            placeholder='Category'
            disabled={filterControlsDisabled}
            className={cn(FILTER_TRIGGER_CLASS, '[&_span]:mx-0 [&_svg]:mx-0 [&_svg]:shrink-0')}
          />
          {filters.isError ? (
            <div role='alert' className='flex items-center gap-2 text-xs text-red-600'>
              <span>Filters could not be loaded.</span>
              <Button type='button' variant='outline' size='sm' onClick={filters.refetch}>Retry filters</Button>
            </div>
          ) : null}
          {dateRangeError ? <p role='alert' className='text-xs text-red-600'>{dateRangeError}</p> : null}
          {filterError ? <p role='alert' className='text-xs text-red-600'>{filterError}</p> : null}
        </div>
      </header>

      <h2 className='mt-7 text-xs font-medium leading-4 text-gray-800'>Overview</h2>
      <OverviewStateShell overview={overview} />

      {overview.data && overview.data.volume.totalTickets > 0 ? (
        <>
          <div className='mt-3 grid gap-4 lg:grid-cols-2'>
            <MetricCard title='Tickets handled by Kim' help='Tickets currently completed by Kim out of all ingested tickets.' className='h-28'>
              <div className='mt-auto flex items-end justify-between gap-4'>
                <p className='text-xl font-semibold leading-7 tabular-nums text-ink'>
                  {overview.data.volume.kimHandledTickets.toLocaleString()}
                  <span className='ml-2 text-sm font-normal text-gray-900'>/ {overview.data.volume.totalTickets.toLocaleString()}</span>
                </p>
                <div
                  role='img'
                  aria-label={`${percent(overview.data.volume.handlingRate)} handled by Kim`}
                  className='absolute right-4 top-4 grid size-20 shrink-0 place-items-center rounded-full'
                  style={{ background: `conic-gradient(var(--color-indigo-700) ${Math.round((overview.data.volume.handlingRate ?? 0) * 360)}deg, var(--color-indigo-300) 0)` }}
                >
                  <div className='grid size-14 place-items-center rounded-full bg-white text-sm font-semibold text-black-300'>
                    {percent(overview.data.volume.handlingRate)}
                  </div>
                </div>
              </div>
            </MetricCard>
            <MetricCard title='Channel' help='How tickets in this range are distributed across channels.' className='min-h-28 lg:h-28'>
              <ChannelDistribution channels={groupChannels(overview.data.volume.channels)} />
            </MetricCard>
          </div>

          <h2 className='mt-11 text-xs font-medium leading-4 text-gray-800'>Quality &amp; Speed</h2>
          <div className='mt-3 grid gap-4 lg:grid-cols-2'>
            <MetricCard title='CSAT' help='Average customer satisfaction score from answered surveys.' className='h-28'>
              <p className='mt-auto text-xl font-semibold leading-7 tabular-nums text-ink'>
                {overview.data.quality.csat.average?.toFixed(1) ?? '—'}
                <span className='ml-1 text-sm font-normal text-gray-900'>/5</span>
              </p>
            </MetricCard>
            <MetricCard title='CSAT Response Rate' help='Currently completed Kim tickets with a CSAT response divided by all currently completed Kim tickets.' className='h-28'>
              <div className='mt-auto flex items-baseline gap-4'>
                <p className='text-xl font-semibold leading-7 tabular-nums text-ink'>{percent(overview.data.quality.csat.responseRate)}</p>
                <span className='text-sm tabular-nums text-gray-900'>
                  {overview.data.quality.csat.answeredTickets.toLocaleString()} / {kimHandledTickets.toLocaleString()}
                </span>
              </div>
            </MetricCard>
          </div>

          <div className='mt-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]'>
            <MetricCard title='Reopen Rate' help='Distinct resolution cycles reopened by a public customer message.' className='min-h-[236px]'>
              <dl className='mt-2 grid flex-1 grid-rows-3'>
                {([
                  { label: 'Overall', metric: overview.data.quality.reopens.overall },
                  { label: 'Regrettable', metric: overview.data.quality.reopens.regrettable },
                  { label: 'Non-regrettable', metric: overview.data.quality.reopens.nonRegrettable },
                ] satisfies Array<{ label: string; metric: BrandHealthRateMetric }>).map(({ label, metric }, index) => (
                    <div key={label} className={cn('flex min-h-[58px] items-center justify-between', index > 0 && 'border-t border-dashed border-gray-300')}>
                      <dt className='text-sm text-gray-900'>{label}</dt>
                      <dd className='text-right'>
                        <span className='block text-xl font-semibold leading-7 tabular-nums text-ink'>{percent(metric.rate)}</span>
                        <span className='block text-xs tabular-nums text-gray-900'>
                          {metric.count.toLocaleString()} / {kimHandledTickets.toLocaleString()}
                        </span>
                      </dd>
                    </div>
                ))}
              </dl>
            </MetricCard>
            <SpeedCard title='FRT' help='Time from the customer turn to the first Kim reply.' metric={overview.data.speed.firstResponse} testId='first-response-card' businessTimeAvailability={overview.data.businessTime.available} />
            <SpeedCard title='Resolution time' help='Time from the customer turn to Sentinel completion.' metric={overview.data.speed.resolution} testId='resolution-card' businessTimeAvailability={overview.data.businessTime.available} />
          </div>
        </>
      ) : null}
    </main>
  );
}
